/*
 * 戒导航 · 统一审核中心 —— 处置回执通知卡片
 *
 * 设计依据：doc/统一审核中心设计方案.md §5.14.3（第 ⑤ 层「呈现层」）/ §5.14.5 / §5.14.9。
 *
 * 一句话职责：把「你的帖子被管理员怎么处理了、为什么不见了」这件事**显示给当事人**。
 *
 * 为什么可以直接读通知里的数据、不需要再请求帖子：
 *   后端 ModerationReceiptBlueprint::getData() 里放的是**渲染所需的全部快照**
 *   （主题标题 / 楼层号 / 原因 / 角色）。这一点是刻意的 ——
 *   帖子可能已被删除或对收件人不可见，任何"再查一次帖子"的做法都会失败。
 *
 * ⚠️ 安全（对应验证用例 V15 的同源要求）：
 *   discussionTitle / reasonText / postExcerpt 都是**纯文本**（后端已做长度截断），
 *   这里只作为文本节点渲染，绝不可用 m.trust / dangerouslySetInnerHTML。
 *
 * ⚠️ 字段契约：下方 ReceiptContent 的字段名必须与
 *   JieDH\Review\Moderation\ModerationReceiptBlueprint::getData() 保持一致；
 *   后端改名时此处必须同步（后端文件里也有同款提醒）。
 *
 * ⚠️ JSX 写法约定（勿改成 import mithril）：
 *   本项目所有扩展都不 import mithril，JSX 由 flarum-webpack-config 的 babel
 *   以 `pragma: 'm'` 编译成对**全局标识符 `m`** 的调用（详见 packages/icon-picker）。
 */
import app from 'flarum/forum/app';
import Notification from 'flarum/forum/components/Notification';

/**
 * 通知数据契约（= Blueprint::getData() 的返回值）。
 */

/**
 * 通知里预览「被处置内容」的字符数。
 *
 * 为什么需要它：光有《主题标题》，当事人分不清"被删的是我哪一条回复"
 *（同一个主题里可能回过很多条）。截一小段开头既能对上号，
 * 又不会把已经删除的内容整段复述给当事人 —— 所以刻意取得很短。
 * 想调整预览长度，改这一个常量即可。
 */
const EXCERPT_PREVIEW_LENGTH = 5;

/**
 * 各处置动作的图标（通知条目**左侧**的类型图标，由核心 Notification 基类的 icon() 渲染）。
 *
 * ⚠️ 图标选取红线（2026-09-17 实机事故修复）：
 *   本表是「通知类型」图标，不是「操作按钮」图标，二者在通知行里**同屏出现**
 *   （左侧类型图标 + 右侧动作按钮，见 vendor/flarum/core/js/src/forum/components/HeaderListItem.tsx）。
 *   曾因 delete 用 'fas fa-trash-alt'、而 packages/notification 的删除按钮也用
 *   'fas fa-trash-alt'，导致每条通知显示两个一模一样的垃圾桶（用户实机反馈）。
 *   因此：
 *     ① 本表禁止使用 'fas fa-trash-alt' / 'fas fa-trash-can'（那是删除按钮的语义，见 packages/notification）；
 *     ② 本表也禁止使用 'fas fa-check'（那是核心「标记已读」按钮的图标）。
 *   新增动作图标前，请先确认它与 core Notification::actionItems() 以及
 *   packages/notification 的删除按钮图标都不重名。
 */
const ICONS = {
  hide: 'fas fa-eye-slash',
  delete: 'fas fa-comment-slash',
  unapprove: 'fas fa-undo',
  ignore_flags: 'fas fa-flag',
  warn: 'fas fa-exclamation-triangle',
  approve: 'fas fa-check'
};

/**
 * 原因文案的兜底顺序：管理员自由文本（reasonText）> 预设原因键（reasonCode）> 不显示。
 */
export default class ModerationReceiptNotification extends Notification {
  /**
   * 通知正文：按「收件人角色 + 处置动作」取对应文案。
   *
   * 文案键形如 jiedh-review.forum.notifications.author_hide_text，
   * 见 packages/review/resources/locale/{zh-Hans,en}.yml。
   */
  content() {
    const data = this.data();
    return app.translator.trans(`jiedh-review.forum.notifications.${data.role}_${data.action}_text`, {
      title: data.discussionTitle
    });
  }
  icon() {
    return ICONS[this.data().action] || 'fas fa-shield-alt';
  }

  /**
   * 点击通知跳转的地址。
   *
   * 与后台审核中心 postUrl() 保持同一种格式：`/d/{主题 id}/{楼层号}`
   * （见设计方案 §5.14.12 验证用例 V28）。
   * 帖子被删除时该链接会落到主题页（帖子本身已不存在），这是预期行为 ——
   * 通知的价值在于**说明为什么不见了**，而不是把已删除的内容变回来。
   */
  href() {
    const {
      discussionId,
      postNumber
    } = this.data();
    if (!discussionId) return '';
    const base = app.forum.attribute('baseUrl') || '';
    return postNumber > 0 ? `${base}/d/${discussionId}/${postNumber}` : `${base}/d/${discussionId}`;
  }

  /**
   * 副标题：显示「被处置的是哪条内容」+「为什么」。
   *
   * 两行都是可选的，全都没有时返回空字符串，通知条目会自动略过该行
   *（Flarum 的 HeaderListItem 会照常渲染空 div，视觉上无高度）。
   *
   * ⚠️ 必须用 div 分行：父容器 .HeaderListItem-excerpt 是
   *   white-space: nowrap + overflow: hidden，靠内联元素是换不了行的。
   */
  excerpt() {
    const preview = this.contentPreview();
    const reason = this.reasonText();
    if (!preview && !reason) return '';
    return m("div", null, preview ? m("div", null, app.translator.trans('jiedh-review.forum.notifications.content_excerpt', {
      excerpt: preview
    })) : null, reason ? m("div", null, app.translator.trans('jiedh-review.forum.notifications.reason', {
      reason
    })) : null);
  }

  /**
   * 被处置内容的前几个字（EXCERPT_PREVIEW_LENGTH 个字）。
   *
   * 两个易踩的坑：
   *   1) 老数据的通知 content 里没有 postExcerpt 字段（后端是后补的），
   *      这里必须容忍 undefined，返回空串让调用方跳过该行；
   *   2) 不能用 String.prototype.slice 直接截 —— 它按 UTF-16 码元切，
   *      遇到 emoji / 生僻字会从中间劈开，显示出半个乱码字符。
   *      用 Array.from 按码点切分才是安全的（与后端的 mb_substr 语义对齐）。
   */
  contentPreview() {
    const source = this.data().postExcerpt;
    if (!source) return '';
    const chars = Array.from(source);
    return chars.length <= EXCERPT_PREVIEW_LENGTH ? source : `${chars.slice(0, EXCERPT_PREVIEW_LENGTH).join('')}…`;
  }

  /**
   * 读取通知携带的数据（防御：后端数据结构异常时不抛错、不白屏）。
   */
  data() {
    const content = this.attrs.notification.content() || {};
    return {
      action: String(content.action || ''),
      reasonCode: content.reasonCode ? String(content.reasonCode) : null,
      reasonText: content.reasonText ? String(content.reasonText) : null,
      discussionId: Number(content.discussionId || 0),
      discussionTitle: String(content.discussionTitle || ''),
      postId: Number(content.postId || 0),
      postNumber: Number(content.postNumber || 0),
      role: String(content.role || 'author'),
      postExcerpt: content.postExcerpt ? String(content.postExcerpt) : ''
    };
  }

  /**
   * 把「预设原因键」翻译成人类可读文案；自由文本优先。
   */
  reasonText() {
    const {
      reasonText,
      reasonCode
    } = this.data();
    if (reasonText) return reasonText;
    if (reasonCode) {
      const key = `jiedh-review.forum.notifications.reason_${reasonCode}`;
      const label = app.translator.trans(key);

      // 未登记的预设键：Flarum 的 translator 会原样返回 key，此时宁可不显示
      return label === key ? '' : String(label);
    }
    return '';
  }
}
flarum.reg.add('jiedh-review', 'forum/components/ModerationReceiptNotification', ModerationReceiptNotification);